import React, {useState} from 'react';
import {Box, Grid, Typography, Paper, useTheme, IconButton, alpha, CircularProgress, ToggleButton, ToggleButtonGroup, Chip, TextField} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {DOMAINS, useDomain} from "../../../contexts/DomainContext";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import HelpModal from "../../Common/HelpModal";
import CompanySummaryCard from './CompanySummaryCard';
import AllKPIStatistics from './AllKPIStatistics';
import CompanyComparisonChart from './CompanyComparisonChart';
import CompanyKpiDetailModal from './CompanyKpiDetailModal';
import { useBranchKPIData } from './hooks';

import NorthGyeonggiCompanySummaryCard from './northGyeonggiBranch/CompanySummaryCard';
import NorthGyeonggiAllKPIStatistics from './northGyeonggiBranch/AllKPIStatistics';
import NorthGyeonggiCompanyComparisonChart from './northGyeonggiBranch/CompanyComparisonChart';

// KPI 모니터링 메인 컴포넌트
const KPIMonitoring = ({ tabId, isNGyeonggi }) => {
  // Theme 및 Context 관련
  const theme = useTheme();
  const {domain} = useDomain();
  const isDarkMode = theme.palette.mode === 'dark';

  // 지부 기준 컴포넌트 및 스타일 설정
  const CompanySummaryCardComponent = isNGyeonggi ? NorthGyeonggiCompanySummaryCard : CompanySummaryCard;
  const AllKPIStatisticsComponent = isNGyeonggi ? NorthGyeonggiAllKPIStatistics : AllKPIStatistics;
  const CompanyComparisonChartComponent = isNGyeonggi ? NorthGyeonggiCompanyComparisonChart : CompanyComparisonChart;
  const gridTemplateColumns = isNGyeonggi ? {
    xs: 'repeat(auto-fit, minmax(360px, 1fr))',    // 모바일: 최소 360px 확보
    sm: 'repeat(auto-fit, minmax(350px, 1fr))',    // 태블릿: 2개
    md: 'repeat(auto-fit, minmax(340px, 1fr))',    // 데스크탑 소: 2~3개
    lg: 'repeat(auto-fit, minmax(330px, 1fr))',    // 데스크탑 대: 3~4개
    xl: 'repeat(auto-fit, minmax(320px, 1fr))',    // 대형 화면: 4개
  } : {
    xs: 'repeat(auto-fit, minmax(320px, 1fr))',    // 모바일: 충분한 너비 확보
    sm: 'repeat(auto-fit, minmax(280px, 1fr))',    // 태블릿: 2-3개
    md: 'repeat(auto-fit, minmax(250px, 1fr))',    // 데스크탑 소: 3-5개
    lg: 'repeat(auto-fit, minmax(220px, 1fr))',    // 데스크탑 대: 4-6개
    xl: 'repeat(auto-fit, minmax(200px, 1fr))'     // 대형: 5-7개
  };
  const kpiDetailModalColorMap = isNGyeonggi? {
    'Q': ['#9C2000', 'rgba(156,32,0,0.89)'],     // 품질 - 적갈색
    'P': ['#88C052', 'rgba(136,192,82,0.89)'],     // 생산성 - 녹색
    'C': ['#607d8b', 'rgba(96,125,139,0.89)'],     // 원가 - 회색
    'D': ['#795548', 'rgba(121,85,72,0.89)'],     // 납기 - 갈색
  } : {
    'Q': ['#4caf50', '#81c784'],     // 품질 - 녹색
    'P': ['#2196f3', '#64b5f6'],     // 생산성 - 파란색
    'C': ['#ff9800', '#ffb74d'],     // 원가 - 주황색
    'D': ['#f44336', '#e57373'],     // 납기 - 빨간색
  };



  // 스타일 관련 함수
  const getTextColor = () => domain === DOMAINS.PEMS ?
      (isDarkMode ? '#f0e6d9' : 'rgba(0, 0, 0, 0.87)') :
      (isDarkMode ? '#b3c5e6' : 'rgba(0, 0, 0, 0.87)');

  const getBgColor = () => domain === DOMAINS.PEMS ?
      (isDarkMode ? 'rgba(45, 30, 15, 0.5)' : 'rgba(252, 235, 212, 0.6)') :
      (isDarkMode ? 'rgba(0, 27, 63, 0.5)' : 'rgba(232, 244, 253, 0.6)');

  const getBorderColor = () => domain === DOMAINS.PEMS ?
      (isDarkMode ? '#3d2814' : '#f5e8d7') :
      (isDarkMode ? '#1e3a5f' : '#e0e0e0');

  // 상태 관리
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [selectedKPIs, setSelectedKPIs] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('DAY'); // 기본: 일별
  const [baseDate, setBaseDate] = useState(new Date()); // 기준일
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedCompanyDetail, setSelectedCompanyDetail] = useState(null);

  // 기간 변경 핸들러
  const handlePeriodChange = (event, newPeriod) => {
    if (newPeriod !== null) {
      setSelectedPeriod(newPeriod);
    }
  };

  // 선택된 기간에 따른 기준 일자 계산
  const getCurrentPeriodText = () => {
    switch(selectedPeriod) {
      case 'DAY':
        return `기준일: ${baseDate.getFullYear()}.${(baseDate.getMonth() + 1).toString().padStart(2, '0')}.${baseDate.getDate().toString().padStart(2, '0')}`;
      
      case 'WEEK':
        const weekStart = new Date(baseDate);
        weekStart.setDate(baseDate.getDate() - baseDate.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        
        return `기준주: ${weekStart.getFullYear()}.${(weekStart.getMonth() + 1).toString().padStart(2, '0')}.${weekStart.getDate().toString().padStart(2, '0')} ~ ${(weekEnd.getMonth() + 1).toString().padStart(2, '0')}.${weekEnd.getDate().toString().padStart(2, '0')}`;
      
      case 'MONTH':
        const month = baseDate.getMonth() + 1;
        return `기준월: ${baseDate.getFullYear()}.${month.toString().padStart(2, '0')}`;
      
      default:
        return '기준 기간';
    }
  };

  // 비교 기간 텍스트 (차트용) - 실제 서버 비교 데이터 기준
  const getComparisonPeriodText = () => {
    switch(selectedPeriod) {
      case 'DAY':
        // 서버에서 실제 비교하는 날짜: (baseDate-1) vs (baseDate-2)
        const currentDay = new Date(baseDate);
        currentDay.setDate(baseDate.getDate() - 1); // 전일
        const previousDay = new Date(baseDate);
        previousDay.setDate(baseDate.getDate() - 2); // 전전일
        return `${previousDay.getMonth() + 1}/${previousDay.getDate()} vs ${currentDay.getMonth() + 1}/${currentDay.getDate()}`;
      
      case 'WEEK':
        const currentWeekStart = new Date(baseDate);
        currentWeekStart.setDate(baseDate.getDate() - baseDate.getDay());
        const currentWeekEnd = new Date(currentWeekStart);
        currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
        
        const previousWeekStart = new Date(currentWeekStart);
        previousWeekStart.setDate(currentWeekStart.getDate() - 7);
        const previousWeekEnd = new Date(currentWeekEnd);
        previousWeekEnd.setDate(currentWeekEnd.getDate() - 7);
        
        return `${previousWeekStart.getMonth() + 1}/${previousWeekStart.getDate()}~${previousWeekEnd.getMonth() + 1}/${previousWeekEnd.getDate()} vs ${currentWeekStart.getMonth() + 1}/${currentWeekStart.getDate()}~${currentWeekEnd.getMonth() + 1}/${currentWeekEnd.getDate()}`;
      
      case 'MONTH':
        const currentMonth = baseDate.getMonth() + 1;
        const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1;
        const previousMonthYear = currentMonth === 1 ? baseDate.getFullYear() - 1 : baseDate.getFullYear();
        
        return `${previousMonthYear}.${previousMonth.toString().padStart(2, '0')} vs ${baseDate.getFullYear()}.${currentMonth.toString().padStart(2, '0')}`;
      
      default:
        return '기간 비교';
    }
  };

  // 지부별 KPI 데이터 조회 (site는 백엔드에서 자동으로 현재 사용자 정보로 설정)
  const {
    companies,
    branchStatistics,
    availableKPIs,
    loading
  } = useBranchKPIData(null, selectedPeriod, baseDate);

  // 회사 상세 보기 핸들러
  const handleCompanyDetailClick = (compCd, companyName) => {
    setSelectedCompanyDetail({
      compCd,
      companyName,
      site: branchStatistics?.site
    });
    setDetailModalOpen(true);
  };

  const handleDetailModalClose = () => {
    setDetailModalOpen(false);
    setSelectedCompanyDetail(null);
  };

  const renderComparisonChart = () => (
      <Grid item xs={12}>
        <Paper
            elevation={1}
            sx={{
              p: 3,
              backgroundColor: '#ffffff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
        >
          <CompanyComparisonChartComponent
              companies={companies}
              selectedKPIs={selectedKPIs}
              availableKPIs={availableKPIs}
              isDarkMode={isDarkMode}
              onKPIChange={setSelectedKPIs}
              selectedPeriod={selectedPeriod}
              periodLabel={
                  {
                    DAY: '일별',
                    WEEK: '주별',
                    MONTH: '월별',
                  }[selectedPeriod] || '일별'
              }
              comparisonPeriodText={getComparisonPeriodText()}
          />
        </Paper>
      </Grid>
  );

  const renderAllKPIStatistics = () => (
      <Grid item xs={12}>
        <AllKPIStatisticsComponent
            data={branchStatistics}
            availableKPIs={availableKPIs}
            loading={loading}
            companies={companies}
        />
      </Grid>
  );

  const renderCompanySummaryCardComponent = () => (
      <Grid item xs={12}>
          <Box sx={{
              display: 'grid',
              gap: {
                  xs: 2,      // 모바일
                  sm: 2.2,    // 태블릿
                  md: 2.5,    // 데스크탑 소
                  lg: 2.8,    // 데스크탑 대
                  xl: 3       // 대형 디스플레이
              },
              py: 1,      // 상하 여유 공간 추가
              // 반응형 그리드: KPI 지표명이 2줄을 넘지 않도록 최소 너비 확보
              gridTemplateColumns,
              // 10개 이상일 때 다음 줄로 넘어감
              maxWidth: '100%',
              overflow: 'hidden'
          }}>
              {companies?.map((company) => (
                  <CompanySummaryCardComponent
                      key={company.companyCode}
                      company={company}
                      availableKPIs={availableKPIs}
                      onDetailClick={handleCompanyDetailClick}
                  />
              ))}
          </Box>
      </Grid>
  );

  return (
    <Box sx={{p: 0, minHeight: '100vh'}}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        gap: { xs: 1.5, md: 0 },
        mb: 2,
        borderBottom: `1px solid ${getBorderColor()}`,
        pb: { xs: 1.5, md: 1 }
      }}>
        {/* 첫 번째 줄: 제목과 배지들 */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          flex: 1, 
          gap: { xs: 1, sm: 1.5, md: 2 },
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          minWidth: 0
        }}>
          <Typography variant="h5" component="h2"
                      sx={{
                        fontWeight: 600,
                        color: getTextColor(),
                        fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                        flexShrink: 0
                      }}>
            지부 KPI 모니터링 대시보드
          </Typography>
          
          {/* 배지들 */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            flexWrap: 'wrap'
          }}>
            {/* 지부 배지 */}
            {branchStatistics?.siteNm && (
              <Chip 
                label={branchStatistics.siteNm}
                sx={{
                  backgroundColor: isDarkMode ? theme.palette.primary.dark : theme.palette.primary.main,
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.75rem', md: '0.9rem' },
                  height: { xs: '28px', md: '32px' },
                  px: 1
                }}
              />
            )}
            
            {/* KPI 지표 수 배지 */}
            {branchStatistics && branchStatistics.indicators && (
              <Chip 
                label={`KPI ${branchStatistics.indicators.length}개`}
                sx={{
                  backgroundColor: '#e8f5e8',
                  color: '#2e7d32',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.7rem', md: '0.8rem' },
                  height: { xs: '24px', md: '28px' }
                }}
              />
            )}
            
            {/* 지부 내 업체 수 배지 */}
            <Chip 
              label={`업체 ${companies?.length || 0}개`}
              sx={{
                backgroundColor: '#fff3e0',
                color: '#f57c00',
                fontWeight: 'bold',
                fontSize: { xs: '0.7rem', md: '0.8rem' },
                height: { xs: '24px', md: '28px' }
              }}
            />
          </Box>
        </Box>
        
        {/* 두 번째 줄: 컨트롤들 (모바일에서는 아래로, 데스크탑에서는 오른쪽) */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          gap: { xs: 1, md: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          width: { xs: '100%', md: 'auto' }
        }}>
          {/* 기간 선택 버튼 */}
          <ToggleButtonGroup
            value={selectedPeriod}
            exclusive
            onChange={handlePeriodChange}
            size="small"
            sx={{ 
              flexShrink: 0,
              '& .MuiToggleButton-root': {
                px: { xs: 1.5, md: 2 },
                fontSize: { xs: '0.8rem', md: '0.875rem' }
              }
            }}
          >
            <ToggleButton value="DAY">일별</ToggleButton>
            <ToggleButton value="WEEK">주별</ToggleButton>
            <ToggleButton value="MONTH">월별</ToggleButton>
          </ToggleButtonGroup>
          
          {/* 기준 일자 정보 및 선택 */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            <Typography variant="body2" sx={{ 
              color: '#666', 
              fontSize: { xs: '0.75rem', md: '0.85rem' },
              textAlign: { xs: 'center', sm: 'left' },
              whiteSpace: { xs: 'normal', sm: 'nowrap' }
            }}>
              {getCurrentPeriodText()}
            </Typography>
            <TextField
              type="date"
              size="small"
              value={baseDate.toISOString().split('T')[0]}
              onChange={(e) => setBaseDate(new Date(e.target.value))}
              sx={{
                '& .MuiInputBase-root': {
                  height: { xs: '36px', md: '32px' },
                  fontSize: { xs: '0.85rem', md: '0.8rem' }
                },
                '& .MuiInputBase-input': {
                  padding: '4px 8px'
                },
                width: { xs: '160px', md: '140px' }
              }}
            />
          </Box>
          
          {/* 도움말 버튼 */}
          <IconButton
              onClick={() => setIsHelpModalOpen(true)}
              sx={{
                ml: { xs: 0, md: 1 },
                color: isDarkMode ? theme.palette.primary.light : theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: isDarkMode
                      ? alpha(theme.palette.primary.light, 0.1)
                      : alpha(theme.palette.primary.main, 0.05)
                },
                flexShrink: 0
              }}
          >
            <HelpOutlineIcon/>
          </IconButton>
        </Box>
      </Box>



      {/* 로딩 표시 */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            KPI 데이터를 불러오는 중...
          </Typography>
        </Box>
      )}

      {/* 메인 컨텐츠 */}
      {!loading && (
        <Grid container spacing={3}>

          {isNGyeonggi ? (
              <>
                {renderComparisonChart()}
                {renderAllKPIStatistics()}
                {renderCompanySummaryCardComponent()}
              </>
          ) : (
              <>
                {renderAllKPIStatistics()}
                {renderCompanySummaryCardComponent()}
                {renderComparisonChart()}
              </>
          )}

          {/* 회사별 요약 카드 섹션 - 반응형 레이아웃 */}

        </Grid>
      )}

      {/* 도움말 모달 */}
      <HelpModal
        open={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
        title="지부 KPI 모니터링 도움말"
      >
        <Typography variant="subtitle1" color="text.secondary" mt={1}>
          지부 내 모든 회사의 KPI 지표를 한눈에 모니터링하는 시스템입니다.
        </Typography>
      </HelpModal>

      {/* 회사별 KPI 상세 모달 */}
      <CompanyKpiDetailModal
        open={detailModalOpen}
        onClose={handleDetailModalClose}
        compCd={selectedCompanyDetail?.compCd}
        companyName={selectedCompanyDetail?.companyName}
        site={selectedCompanyDetail?.site}
        colorMap={kpiDetailModalColorMap}
      />
    </Box>
  );
};

export default KPIMonitoring; 